/**
 * Shared validation utilities.
 * Use these instead of duplicating validation logic across packages.
 */

import { isDisposableEmailDomain as isUpstreamDisposableDomain } from "disposable-email-domains-js";

/**
 * Email validation regex - supports common email formats including:
 * - Standard formats (user@domain.com)
 * - Plus addressing (user+tag@domain.com)
 * - Dots and hyphens (user.name@sub-domain.example.com)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Check if a string is a valid email address.
 * @param email - The email string to validate
 * @returns true if valid email format, false otherwise
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return EMAIL_REGEX.test(email.toLowerCase().trim());
}

/**
 * Validate an email address and return an error message if invalid.
 * Useful for form validation where you need user-friendly error messages.
 * @param email - The email string to validate
 * @returns Error message string if invalid, null if valid
 */
export function validateEmail(email: string): string | null {
  if (!email || !email.trim()) {
    return "Email address is required.";
  }

  if (!isValidEmail(email)) {
    return "Please enter a valid email address.";
  }

  return null;
}

/**
 * Validate multiple email addresses.
 * @param emails - Array of email strings to validate
 * @returns Array of invalid emails, or empty array if all are valid
 */
export function getInvalidEmails(emails: string[]): string[] {
  return emails.filter(email => !isValidEmail(email));
}

const PUBLIC_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  "gmail.com", "googlemail.com",
  // Microsoft
  "outlook.com", "hotmail.com", "live.com", "msn.com", "hotmail.co.uk", "hotmail.fr", "outlook.co.uk",
  // Yahoo
  "yahoo.com", "yahoo.co.uk", "yahoo.co.in", "yahoo.ca", "yahoo.com.au", "ymail.com", "rocketmail.com",
  // Apple
  "icloud.com", "me.com", "mac.com",
  // AOL
  "aol.com", "aim.com",
  // Privacy-focused
  "protonmail.com", "proton.me", "tutanota.com", "tuta.com", "fastmail.com",
  // Other providers
  "zoho.com", "zohomail.com", "mail.com", "gmx.com", "gmx.net", "yandex.com", "yandex.ru",
  // ISP-based
  "comcast.net", "verizon.net", "att.net", "sbcglobal.net", "cox.net", "charter.net",
]);

export function isPublicEmailDomain(domain: string): boolean {
  if (!domain || typeof domain !== "string") {
    return false;
  }
  return PUBLIC_EMAIL_DOMAINS.has(domain.toLowerCase().trim());
}

// Disposable email domains we've personally observed abusing Brev that aren't
// yet in the upstream disposable-email-domains-js list. Keep this list small
// and targeted — the upstream package auto-updates with ~5000 community-
// maintained domains, so only add entries here that slip through.
//
// When adding an entry, prefer to also open a PR upstream
// (https://github.com/mziyut/disposable-email-domains-js) so other projects
// benefit and we can drop the local override on a future bump.
const SUPPLEMENTARY_DISPOSABLE_DOMAINS: ReadonlySet<string> = new Set([
  "spotshops.com",
]);

/**
 * Check whether a domain belongs to a disposable / throwaway email provider
 * (mailinator.com, 10minutemail.com, etc.). Combines the upstream
 * community-maintained blocklist with a small project-maintained supplement
 * for domains we've seen abuse Brev before they landed upstream.
 *
 * Used at every auth entry point to block signup/login abuse — works as a
 * local fast-path independent of WorkOS Radar, so throwaway accounts are
 * rejected even when Radar is disabled, misconfigured, or failing open.
 */
export function isDisposableEmailDomain(domain: string): boolean {
  if (!domain || typeof domain !== "string") {
    return false;
  }
  const normalized = domain.toLowerCase().trim();
  if (SUPPLEMENTARY_DISPOSABLE_DOMAINS.has(normalized)) return true;
  return isUpstreamDisposableDomain(normalized);
}

const SOCIAL_OAUTH_METHODS: ReadonlySet<string> = new Set([
  "GoogleOAuth",
  "MicrosoftOAuth",
  "GitHubOAuth",
  "AppleOAuth",
  "LinkedInOAuth",
  "DiscordOAuth",
  "BitbucketOAuth",
  "GitLabOAuth",
  "SlackOAuth",
]);

export function isSocialOAuthMethod(method: string | undefined): boolean {
  if (!method || typeof method !== "string") {
    return false;
  }
  return SOCIAL_OAUTH_METHODS.has(method);
}
